import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import PlaceCard from "../components/PlaceCard";
import { Button } from "../components/ui/button";
import { RESTRICTIONS } from "../constants/restrictions";
import { ShieldCheck, Sparkles, MapPin, MessageSquareHeart, FileSignature, ExternalLink } from "lucide-react";

const HERO_BG_FALLBACK = "https://images.pexels.com/photos/27177294/pexels-photo-27177294.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function isExternal(url) {
  return /^https?:\/\//i.test(url || "");
}

function BannerCTA({ label, link, style, testId }) {
  if (!label) return null;
  const cls = "inline-flex items-center gap-2 rounded-full font-bold px-7 py-4 text-base shadow-lg hover:scale-[1.02] transition-transform";
  if (isExternal(link)) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls} style={style}>
        {label} <ExternalLink className="w-4 h-4" />
      </a>
    );
  }
  return (
    <Link to={link || "#"} data-testid={testId} className={cls} style={style}>
      {label}
    </Link>
  );
}

function HeroBanner({ banner }) {
  if (!banner || banner.enabled === false) return null;
  const bg = banner.image_path ? fileUrl(banner.image_path) : HERO_BG_FALLBACK;
  return (
    <section className="relative overflow-hidden" data-testid="hero-banner">
      <div className="absolute inset-0">
        <img src={bg} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${banner.bg_color}dd 0%, ${banner.bg_color}b3 60%, ${banner.bg_color}99 100%)` }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
        <div className="max-w-3xl animate-fade-up" style={{ color: banner.text_color }}>
          {banner.badge && (
            <span className="inline-flex items-center gap-2 backdrop-blur-sm rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em]" style={{ background: `${banner.text_color}18`, border: `1px solid ${banner.text_color}33` }}>
              <Sparkles className="w-3.5 h-3.5" /> {banner.badge}
            </span>
          )}
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6 drop-shadow">
            {banner.title} {banner.highlight && <span style={{ color: banner.accent_color }}>{banner.highlight}</span>}
          </h1>
          {banner.description && (
            <p className="mt-6 text-lg sm:text-xl max-w-2xl leading-relaxed" style={{ color: `${banner.text_color}e6` }}>
              {banner.description}
            </p>
          )}
          <div className="mt-10 flex flex-wrap gap-3">
            <BannerCTA label={banner.button_label} link={banner.button_link} testId="hero-primary-btn"
              style={{ background: banner.button_bg, color: banner.button_text }} />
            {banner.secondary_label && (
              isExternal(banner.secondary_link) ? (
                <a href={banner.secondary_link} target="_blank" rel="noopener noreferrer" data-testid="hero-secondary-btn"
                  className="inline-flex items-center gap-2 border-2 rounded-full font-bold px-7 py-4 text-base hover:bg-white hover:text-brand-primary transition-colors"
                  style={{ borderColor: banner.text_color, color: banner.text_color }}>
                  {banner.secondary_label}
                </a>
              ) : (
                <Link to={banner.secondary_link || "#"} data-testid="hero-secondary-btn"
                  className="inline-flex items-center gap-2 border-2 rounded-full font-bold px-7 py-4 text-base hover:bg-white hover:text-brand-primary transition-colors"
                  style={{ borderColor: banner.text_color, color: banner.text_color }}>
                  {banner.secondary_label}
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function PetitionBanner({ banner }) {
  if (!banner || banner.enabled === false) return null;
  const style = banner.image_path ? { backgroundImage: `linear-gradient(135deg, ${banner.bg_color}dd, ${banner.bg_color}b3), url(${fileUrl(banner.image_path)})`, backgroundSize: "cover", backgroundPosition: "center", color: banner.text_color } : { background: banner.bg_color, color: banner.text_color };
  return (
    <section data-testid="petition-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
      <div className="rounded-3xl overflow-hidden relative p-8 sm:p-12 shadow-lg" style={style}>
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full blur-3xl" style={{ background: `${banner.accent_color}33` }} aria-hidden />
        <div className="absolute -left-20 bottom-0 w-72 h-72 rounded-full blur-3xl" style={{ background: `${banner.accent_color}22` }} aria-hidden />
        <div className="relative grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3">
            {banner.badge && (
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] backdrop-blur" style={{ background: `${banner.text_color}18`, border: `1px solid ${banner.text_color}33` }}>
                <FileSignature className="w-3.5 h-3.5" /> {banner.badge}
              </span>
            )}
            <h2 className="mt-4 font-heading font-black text-3xl sm:text-4xl leading-tight">
              {banner.title}{banner.highlight && <> <span style={{ color: banner.accent_color }}>{banner.highlight}</span></>}
            </h2>
            {banner.description && (
              <p className="mt-3 leading-relaxed max-w-xl" style={{ color: `${banner.text_color}e6` }}>{banner.description}</p>
            )}
          </div>
          <div className="md:col-span-2 flex md:justify-end">
            <BannerCTA label={banner.button_label} link={banner.button_link} testId="petition-btn"
              style={{ background: banner.button_bg, color: banner.button_text }} />
          </div>
        </div>
      </div>
    </section>
  );
}

function CustomBanner({ banner }) {
  if (!banner || banner.enabled === false) return null;
  const style = banner.image_path
    ? { backgroundImage: `linear-gradient(135deg, ${banner.bg_color}dd, ${banner.bg_color}b3), url(${fileUrl(banner.image_path)})`, backgroundSize: "cover", backgroundPosition: "center", color: banner.text_color }
    : { background: banner.bg_color, color: banner.text_color };
  return (
    <section data-testid={`custom-banner-${banner.banner_id}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="rounded-3xl overflow-hidden p-8 sm:p-10 shadow-lg" style={style}>
        <div className="grid md:grid-cols-5 gap-6 items-center">
          <div className="md:col-span-3">
            {banner.badge && <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]" style={{ background: `${banner.accent_color}33`, color: banner.text_color, border: `1px solid ${banner.accent_color}55` }}>{banner.badge}</span>}
            <h2 className="mt-3 font-heading font-black text-2xl sm:text-3xl leading-tight">
              {banner.title} {banner.highlight && <span style={{ color: banner.accent_color }}>{banner.highlight}</span>}
            </h2>
            {banner.description && <p className="mt-2 opacity-90 max-w-xl">{banner.description}</p>}
          </div>
          {banner.button_label && (
            <div className="md:col-span-2 flex md:justify-end">
              <BannerCTA label={banner.button_label} link={banner.button_link} testId={`custom-banner-btn-${banner.banner_id}`}
                style={{ background: banner.button_bg, color: banner.button_text }} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function BannersAt({ list, placement }) {
  const filtered = useMemo(
    () => list.filter((b) => b.placement === placement && b.enabled !== false),
    [list, placement],
  );
  return (
    <>
      {filtered.map((b) => (
        <CustomBanner key={b.banner_id} banner={b} />
      ))}
    </>
  );
}

export default function Landing() {
  const [places, setPlaces] = useState([]);
  const [stats, setStats] = useState({ approved_places: 0, approved_reviews: 0 });
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    api.get("/places?status=approved").then((r) => setPlaces(r.data.slice(0, 6))).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
  }, []);

  const bannerMap = useMemo(() => {
    const map = {};
    banners.forEach((b) => { map[b.banner_id] = b; });
    return map;
  }, [banners]);
  const hero = bannerMap.hero;

  return (
    <div data-testid="landing-page">
      <HeroBanner banner={hero} />

      {/* Stats block (only when hero renders) */}
      {hero?.enabled !== false && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="bg-white rounded-3xl border border-brand-border shadow-lg p-6 flex flex-wrap gap-8 justify-around">
            <div className="text-center"><div className="font-heading font-black text-3xl text-brand-primary">{stats.approved_places}</div><div className="text-xs uppercase tracking-wider font-bold text-brand-textMuted">Lugares aprovados</div></div>
            <div className="text-center"><div className="font-heading font-black text-3xl text-brand-primary">{stats.approved_reviews}</div><div className="text-xs uppercase tracking-wider font-bold text-brand-textMuted">Avaliações verificadas</div></div>
            <div className="text-center"><div className="font-heading font-black text-3xl text-brand-primary">3</div><div className="text-xs uppercase tracking-wider font-bold text-brand-textMuted">Restrições atendidas</div></div>
          </div>
        </div>
      )}

      <BannersAt list={banners} placement="home_top" />
      <PetitionBanner banner={bannerMap.petition} />

      {/* Restriction categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-secondary">Para quem é</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-text mt-2">Conheça cada restrição</h2>
          </div>
          <Link to="/explorar" className="font-bold text-brand-primary hover:text-brand-primaryHover">Ver tudo →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {RESTRICTIONS.map((r) => (
            <Link
              key={r.value}
              to={`/topico/${r.value}`}
              data-testid={`category-${r.value}`}
              className="group rounded-3xl border border-brand-border p-8 bg-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
              style={{ background: `linear-gradient(135deg, ${r.bg} 0%, #ffffff 100%)` }}
            >
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl" style={{ background: r.bg }}>{r.emoji}</div>
              <h3 className="mt-6 font-heading font-bold text-2xl text-brand-text">{r.label}</h3>
              <p className="mt-2 text-sm text-brand-textMuted">Saiba o que é, dicas de cuidado e veja lugares confiáveis {r.short.toLowerCase()}.</p>
              <span className="inline-block mt-6 font-bold text-sm" style={{ color: r.text }}>Conhecer {r.label} →</span>
            </Link>
          ))}
        </div>
      </section>

      <BannersAt list={banners} placement="home_middle" />

      {/* Featured places */}
      <section className="bg-white border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-secondary">Recém aprovados</span>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-text mt-2">Lugares em destaque</h2>
            </div>
            <Link to="/explorar" className="font-bold text-brand-primary hover:text-brand-primaryHover">Ver todos →</Link>
          </div>
          {places.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-brand-border p-12 text-center text-brand-textMuted">
              <p className="font-bold text-lg text-brand-text">Ainda não há lugares aprovados.</p>
              <p className="mt-2">Seja a primeira pessoa a indicar um restaurante seguro!</p>
              <Link to="/cadastrar-lugar" className="inline-block mt-6">
                <Button className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">Cadastrar lugar</Button>
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-places-grid">
              {places.map((p) => <PlaceCard key={p.place_id} place={p} />)}
            </div>
          )}
        </div>
      </section>

      <BannersAt list={banners} placement="home_bottom" />

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-secondary">Como funciona</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-text mt-2">Confiável porque é moderado</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: MapPin, title: "1. Indique", text: "Cadastre um lugar seguro que você visitou e marque para quais restrições é apropriado." },
            { icon: ShieldCheck, title: "2. Moderamos", text: "Nossos moderadores conferem as informações antes do lugar aparecer publicamente." },
            { icon: MessageSquareHeart, title: "3. Comunidade", text: "Outros usuários compartilham experiências, também passando por moderação." },
          ].map((s) => (
            <div key={s.title} className="rounded-3xl bg-white border border-brand-border p-8 shadow-sm">
              <span className="w-12 h-12 rounded-2xl bg-brand-primary/10 text-brand-primary flex items-center justify-center"><s.icon className="w-6 h-6" /></span>
              <h3 className="font-heading font-bold text-xl mt-5 text-brand-text">{s.title}</h3>
              <p className="text-brand-textMuted mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
